import type {
  CheckSelectionState,
  ExceptionButton,
  SelectedEntityWithException,
} from "~/components/exceptions/types";
import { ExceptionTarget } from "~/components/exceptions/types";
import { ExceptionMutationAction } from "~/operations";
import {
  NO_RISK_EXCEPTION,
  targetLabelMap,
} from "~/components/exceptions/constants";

type UseExceptionsTooltipParams = {
  selectedEntities: SelectedEntityWithException[];
  showScopeButtons: boolean;
  showExceptionButtons: boolean;
  target: ExceptionTarget;
};

export function useExceptionsButtons(params: UseExceptionsTooltipParams) {
  const { selectedEntities, showScopeButtons, showExceptionButtons, target } =
    params;

  const hasEnabledSelectedCheck = selectedEntities.some(
    (check) => !check.exception,
  );

  const hasOutOfScopeCheck = selectedEntities.some(
    (check) => check.exception?.action === ExceptionMutationAction.OutOfScope,
  );

  const hasRiskAcceptedOrDisabledChecks = selectedEntities.some((check) =>
    NO_RISK_EXCEPTION.includes(
      check.exception?.action as ExceptionMutationAction,
    ),
  );

  const hasAllChecksRiskAcceptedOrDisabled = selectedEntities.every((check) =>
    NO_RISK_EXCEPTION.includes(
      check.exception?.action as ExceptionMutationAction,
    ),
  );

  const hasAllChecksOutOfScope = selectedEntities.every(
    (check) => check.exception?.action === ExceptionMutationAction.OutOfScope,
  );

  const hasAllChecksEnabled = selectedEntities.every(
    (check) => !check.exception,
  );

  // We need to determine in which state is currently our toolbar buttons to know which of them show/hide/disable
  // Also to determine which text to display in a tooltip for different combinations (exception + out of scope selections etc...)
  function getExceptionSelectionState(): CheckSelectionState {
    const isOnlyEnabled = selectedEntities.every(
      (check) => check.exception?.action === ExceptionMutationAction.Enable,
    );

    const isOnlyOutOfScopeSelected = selectedEntities.every(
      (check) => check.exception?.action === ExceptionMutationAction.OutOfScope,
    );

    const isOnlyRiskAcceptedOrDisabled = selectedEntities.every((check) =>
      NO_RISK_EXCEPTION.includes(
        check.exception?.action as ExceptionMutationAction,
      ),
    );

    if (isOnlyEnabled) {
      return "ENABLED";
    }

    if (isOnlyOutOfScopeSelected) {
      return "OUT_OF_SCOPE";
    }

    if (isOnlyRiskAcceptedOrDisabled) {
      return "RISK_ACCEPTED_OR_DISABLED";
    }

    if (hasOutOfScopeCheck && hasRiskAcceptedOrDisabledChecks) {
      return "MIXED";
    }

    if (hasEnabledSelectedCheck && hasRiskAcceptedOrDisabledChecks) {
      return "ENABLED_WITH_RISK_ACCEPTED_OR_DISABLED";
    }

    if (hasEnabledSelectedCheck && hasOutOfScopeCheck) {
      return "ENABLED_WITH_OUT_OF_SCOPE";
    }

    return "ENABLED";
  }

  function getButtonsTooltipBySelectionState(): Record<
    ExceptionButton,
    string
  > {
    switch (getExceptionSelectionState()) {
      case "RISK_ACCEPTED_OR_DISABLED":
        return {
          REMOVE_EXCEPTION: "",
          SET_EXCEPTION: `You can't set an exception because selected ${targetLabelMap[target]}s already contain one.`,
          SET_IN_SCOPE: "",
          SET_OUT_OF_SCOPE: `You can't set an ${targetLabelMap[target]} in scope because it contains an exception.`,
        };
      case "OUT_OF_SCOPE":
        return {
          REMOVE_EXCEPTION: "",
          SET_EXCEPTION: `You can't set an exception because this ${targetLabelMap[target]} is out of scope; it's already excluded from compliance with this framework`,
          SET_IN_SCOPE: "",
          SET_OUT_OF_SCOPE: "",
        };
      case "MIXED":
      case "ENABLED_WITH_RISK_ACCEPTED_OR_DISABLED":
      case "ENABLED_WITH_OUT_OF_SCOPE":
      case "RISK_ACCEPTED_OR_DISABLED_WITH_OUT_OF_SCOPE":
        const text = `You can't make bulk changes to these ${
          targetLabelMap[target]
        }s because they are in different states (${
          showScopeButtons ? "in scope, out of scope, or " : ""
        }with or without exceptions). To make bulk changes, select only ${
          targetLabelMap[target]
        }s that are in the same state.`;
        return {
          REMOVE_EXCEPTION: text,
          SET_EXCEPTION: text,
          SET_IN_SCOPE: text,
          SET_OUT_OF_SCOPE: text,
        };
      case "ENABLED":
      default:
        return {
          REMOVE_EXCEPTION: "",
          SET_EXCEPTION: "",
          SET_IN_SCOPE: "",
          SET_OUT_OF_SCOPE: "",
        };
    }
  }

  const exceptionsButtonsVisibility: Record<ExceptionButton, boolean> = {
    REMOVE_EXCEPTION: hasRiskAcceptedOrDisabledChecks && showExceptionButtons,
    SET_EXCEPTION: showExceptionButtons,
    SET_IN_SCOPE: hasOutOfScopeCheck && showScopeButtons,
    SET_OUT_OF_SCOPE: hasEnabledSelectedCheck && showScopeButtons,
  };

  const exceptionsButtonsAvailability: Record<ExceptionButton, boolean> = {
    REMOVE_EXCEPTION: hasAllChecksRiskAcceptedOrDisabled,
    SET_EXCEPTION: hasAllChecksEnabled,
    SET_IN_SCOPE: hasAllChecksOutOfScope,
    SET_OUT_OF_SCOPE: hasAllChecksEnabled,
  };

  const exceptionsButtonsTooltipText = getButtonsTooltipBySelectionState();

  return {
    exceptionsButtonsVisibility,
    exceptionsButtonsAvailability,
    exceptionsButtonsTooltipText,
  };
}
